import { useState } from 'react'
import type { ReactElement } from 'react'
import { useSearchParams } from 'react-router-dom'

import { EmptyState, ErrorState, Select, Spinner } from '../../components'
import { trpc } from '../../trpc'
import { AdminPageHeader, adminStyles as styles } from '../admin/AdminPage'
import { ChangesTable, ENTITY_LABEL } from '../admin/ChangesTable'
import type { ConfigChangeEntity } from '../admin/ChangesTable'

/** The planner's configuration only: a role's history is the roles screen's (section `admin`). */
type ConfigurationEntity = Exclude<ConfigChangeEntity, 'role'>

const ENTITIES: Array<ConfigurationEntity> = [
  'machine_group',
  'plan_machine',
  'quality_rule',
  'special_schedule',
  'bm_homologation',
  'planner_settings',
  'mep_settings',
  'mep_day_team',
  'racionado_settings',
  'chill_slot',
  'pesaje_settings',
]

const ALL = ''

const isConfigurationEntity = (value: string): value is ConfigurationEntity =>
  ENTITIES.some((entity) => entity === value)

/** How far back the history reaches on screen: the API's own cap. */
const CHANGES_LISTED = 200

/** The query parameter a link opens the history filtered by: `?tabla=mep_day_team`. */
export const CHANGES_TABLE_PARAM = 'tabla'

/**
 * Every write to the planner's configuration, newest first: who, when, and what
 * changed. A link can open it filtered by one table (`?tabla=`).
 */
export const ConfigChangesPage = (): ReactElement => {
  const [searchParams] = useSearchParams()
  const [entity, setEntity] = useState<ConfigurationEntity | typeof ALL>(() => {
    const linked = searchParams.get(CHANGES_TABLE_PARAM) ?? ALL
    return isConfigurationEntity(linked) ? linked : ALL
  })

  const changesQuery = trpc.configuration.listChanges.useQuery({
    ...(entity === ALL ? {} : { entity }),
    limit: CHANGES_LISTED,
  })

  let body: ReactElement
  if (changesQuery.isPending) {
    body = <Spinner label="Cargando el historial…" />
  } else if (changesQuery.isError) {
    body = <ErrorState error={changesQuery.error} onRetry={() => void changesQuery.refetch()} />
  } else if (changesQuery.data.length === 0) {
    body = (
      <EmptyState
        title="Sin cambios"
        message={entity === ALL ? 'Nadie ha cambiado todavía la configuración desde aquí.' : 'Nadie ha cambiado todavía esta tabla desde aquí.'}
      />
    )
  } else {
    body = <ChangesTable changes={changesQuery.data} caption="Historial de cambios de la configuración" />
  }

  return (
    <section className={styles['admin-page']}>
      <AdminPageHeader
        title="Historial de cambios"
        intro={`Los últimos ${CHANGES_LISTED} cambios de la configuración del planificador, el más reciente primero.`}
      />
      <Select
        label="Qué tabla"
        options={[
          { value: ALL, label: 'Todas' },
          ...ENTITIES.map((value) => ({ value, label: ENTITY_LABEL[value] })),
        ]}
        value={entity}
        onChange={(event) => {
          const { value } = event.target
          setEntity(isConfigurationEntity(value) ? value : ALL)
        }}
      />
      {body}
    </section>
  )
}
